import { useEffect, useRef, useState } from "react";
import {
  Button,
  Center,
  Flex,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconMicrophoneFilled,
  IconPlayerStopFilled,
} from "@tabler/icons-react";
import PayWallModal from "@/components/Case/PayWallModal";
import ConfirmCancelRecordDialog from "@/components/Case/Recording/ConfirmCancelRecordDialog";
import useUploadRecording from "@/hooks/useUploadRecording";
import { useUserProvider } from "@/utils/UserProvider";
import { checkIfMaxTranscriptions } from "@/utils/checkSubscriptionUsage";
import {
  appShowNotification,
  saveQuickRecordingItemToIndexedDB,
} from "@/utils/utils";
import { AudioDatabase } from "@/utils/utils";
import EditRecordingNameModal from "./EditRecordingNameModal";
import RecordControls from "./RecordControls";
import WaveformVisualizer from "./WaveformVisualizer";

const generateRecordingName = () => {
  const timestamp = new Date().toLocaleTimeString("en-US", {
    hour12: false,
  });
  return "Recording" + timestamp.replace(/\s/g, "").replace(/:\s*/g, "");
};

const Record = ({ 
  onChangeTab
}: { 
  onChangeTab: (tab: string) => void;
}) => {
  const { amplifyUser, revcatUser } = useUserProvider();
  const { handleUploadRecording } = useUploadRecording();
  const audioContext = useRef<any>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const mediaStream = useRef<any>(null);
  const [showPayWall, setShowPayWall] = useState(false);
  const [recording, setRecording] = useState(false);
  const shouldUploadRecordRef = useRef(false);
  const recordingNameRef = useRef(generateRecordingName());
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorder = useRef<any>(null);
  const startTimeRef = useRef<any>([]);
  const audioChunks = useRef<any>([]);
  const timer = useRef<any>(null);
  const [openedCancelRecord, setOpenedCancelRecord] = useState(false);
  const [openedEditRecordingNameModal, setOpenedEditRecordingNameModal] =
    useState(false);
  const isMobile = useMediaQuery("(max-width: 50em)");

  useEffect(() => {
    if (recording) {
      const timer = setTimeout(() => {
        stopRecording({ isUploadRecord: true });
      }, 2700000); // 30 minutes in ms
      return () => clearTimeout(timer);
    }
  }, [recording]);

  const handleStartRecordingPress = async () => {
    if (await checkIfMaxTranscriptions(revcatUser, amplifyUser)) {
      setShowPayWall(true);
      appShowNotification({
        type: "error",
        title:
          "You have reached the limit.\nPlease upgrade your subscription from your Profile",
      });
      return;
    }
    startRecording();
  };

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (timeInSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  async function startRecording() {
    try {
      mediaStream.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      mediaRecorder.current = new MediaRecorder(mediaStream.current, {
        mimeType: "audio/webm",
      });

      setRecording(true);
      startTimeRef.current = Date.now();

      mediaRecorder.current.ondataavailable = (event: any) => {
        audioChunks.current.push(event.data);
      };

      const audioContext = new AudioContext();

      const mediaSource = audioContext.createMediaStreamSource(
        mediaStream.current,
      );
      const newAnalyser = audioContext.createAnalyser();
      newAnalyser.fftSize = 64;
      mediaSource.connect(newAnalyser);
      setAnalyser(newAnalyser);

      mediaRecorder.current.onstop = async () => {
        if (shouldUploadRecordRef.current == false) {
          if (mediaStream.current) {
            mediaStream.current
              .getTracks()
              .forEach((track: any) => track?.stop());
          }
          audioChunks.current = [];
          clearInterval(timer.current);
          setRecordingTime(0);
          return;
        }
        const endTime = Date.now();
        const duration = (endTime - startTimeRef.current) / 1000;
        const audioBlob = new Blob(audioChunks.current, { type: "audio/webm" });
        const objectToURL = URL.createObjectURL(audioBlob);

        // Generate storage path for quick recordings (must match S3Trigger expected format)
        // S3Trigger expects: userId/locationId/patientId/caseId/recordType/filename
        // For QuickRecord: userId/locationId/null/null/COMMUNICATION/filename
        const timestamp = new Date().getTime();
        const storagePath = `${amplifyUser.id}/${amplifyUser.locationId}/null/null/COMMUNICATION/${recordingNameRef.current}-${timestamp}.webm`;

        const newRecording = {
          name: recordingNameRef.current,
          duration: duration,
          date: Date.now(),
          file: objectToURL,
          storagePath: storagePath,
          useTemplate: false,
          templateId: undefined, // Match the type expected by handleUploadRecording
          blob: audioBlob,
          caseId: null, // QuickRecord has no case initially
        };

        // Save to IndexedDB as backup
        saveQuickRecordingItemToIndexedDB(
          `${AudioDatabase.PREFIX_QUICK_RECORD}${timestamp}`,
          newRecording,
        );


        try {
          const uploadSuccess = await handleUploadRecording(
            newRecording,
            amplifyUser.audioRetentionPeriod || 30,
          );
          
          if (uploadSuccess) {
            appShowNotification({
              type: "success",
              title: "Recording saved successfully",
              message: "Your recording has been saved successfully",
            });
          } else {
            appShowNotification({
              type: "warning",
              title: "Recording saved locally",
              message: "Recording saved to device, upload will retry later",
            });
          }
        } catch (error) {
          appShowNotification({
            type: "warning",
            title: "Recording saved locally",
            message: "Recording saved to device, upload will retry later",
          });
        }

        onChangeTab("recordings");
        // No manual refresh needed - subscriptions handle everything

        if (mediaStream.current) {
          mediaStream.current
            .getTracks()
            .forEach((track: any) => track?.stop());
        }
        audioChunks.current = [];
        clearInterval(timer.current);
        setRecordingTime(0);
        recordingNameRef.current = generateRecordingName();
      };
      mediaRecorder.current.start();
      setRecording(true);
      setIsPaused(false);
      timer.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  }

  const onCancelRecord = () => {
    shouldUploadRecordRef.current = false;
    stopRecording({ isUploadRecord: false });
  };

  async function cancelRecording() {
    setOpenedCancelRecord(true);
  }

  const pauseRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
      mediaRecorder.current.pause();
      setIsPaused(true);
      clearInterval(timer.current);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === "paused") {
      mediaRecorder.current.resume();
      setIsPaused(false);
      timer.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    }
  };

  const stopRecording = ({
    isUploadRecord = true,
    openEditRecordingNameModal = false,
  }) => {
    shouldUploadRecordRef.current = isUploadRecord;
    if (openEditRecordingNameModal && isUploadRecord) {
      pauseRecording();
      setOpenedEditRecordingNameModal(true);
      return;
    }
    if (mediaRecorder.current) {
      setRecording(false);
      setIsPaused(false);
      clearInterval(timer.current);
      setTimeout(() => {
        mediaRecorder.current.stop();
      }, 1000);
    }
    if (audioContext.current) {
      audioContext.current.close();
    }
  };

  const handleSaveRecording = (value: string) => {
    recordingNameRef.current = value;
    stopRecording({ isUploadRecord: true, openEditRecordingNameModal: false });
  };

  return (
    <Stack h="100%">
      <Group justify="space-between" p="md">
        <Title size={"20"} c={"#2D2D2D"}>
          New Recording
        </Title>
        <Button
          variant="subtle"
          pr={5}
          pl={5}
          onClick={() => {
            setOpenedEditRecordingNameModal(true);
          }}
        >
          Edit
        </Button>
      </Group>
      <Center pt={50} pb={30}>
        <Flex
          direction={"column"}
          align={"center"}
          flex={1}
          style={{ width: "100%" }}
        >
          <Paper
            bg={"#003554"}
            radius={"50%"}
            w={150}
            h={150}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "3px solid #FFFFFF",
              boxShadow: "none",
              position: "relative",
              cursor: "pointer",
            }}
            onClick={() => {
              recording
                ? stopRecording({
                    isUploadRecord: true,
                    openEditRecordingNameModal: true,
                  })
                : handleStartRecordingPress();
            }}
          >
            {recording ? (
              <IconPlayerStopFilled size={"80"} color="white" />
            ) : (
              <IconMicrophoneFilled size={"80"} color="white" />
            )}
          </Paper>
          {recording && (
            <Text w={150} fz={30} fw={800} ta={"center"} mt={15} mb={35}>
              {formatTime(recordingTime)}
            </Text>
          )}
          <Paper w={"100%"} radius={"md"} shadow="none">
            <Flex
              h={150}
              bg={recording ? "#E6E6E6" : "transparent"}
              justify={"center"}
              align={"center"}
              w={"100%"}
              px={10}
              mt={recording ? 0 : 30}
            >
              <WaveformVisualizer analyser={analyser} isRecording={recording} />
            </Flex>
          </Paper>
          {recording && (
            <RecordControls
              cancelRecording={cancelRecording}
              isPaused={isPaused}
              resumeRecording={resumeRecording}
              pauseRecording={pauseRecording}
              stopRecording={stopRecording}
            />
          )}
        </Flex>
        <ConfirmCancelRecordDialog
          openedModal={openedCancelRecord}
          setOpenedModal={setOpenedCancelRecord}
          onCancel={onCancelRecord}
        />
        {amplifyUser && (
          <PayWallModal
            modalOpened={showPayWall}
            setModalOpened={setShowPayWall}
          />
        )}
      </Center>
      <EditRecordingNameModal
        modalOpened={openedEditRecordingNameModal}
        setModalOpened={(value: boolean) => {
          setOpenedEditRecordingNameModal(value);
        }}
        isMobile={isMobile!}
        initialRecordingName={recordingNameRef.current}
        setRecordingName={handleSaveRecording}
      />
    </Stack>
  );
};

export default Record;
